import { Button, Checkbox, Divider, InputNumber, Select, Slider, Tabs } from 'antd';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';
import React, { useState, useEffect, useRef } from 'react';
import { Col, Row } from 'antd/lib/grid';
import { CaretLeftFilled } from '@ant-design/icons';

import AudioPlayButton from './audio-play-button';
import getLabelDisplayName from 'utils/label-display';
import AnnotationVoiceInput from 'components/voice-input/annotation-voice-input';

import { config_V2, getFilledFullText_V2, toDisplay_V2 } from '../phase2-annotation-conf';
import { config_V1, getFilledFullText_V1, toDisplay_V1 } from '../phase2-annotation-conf_v1';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

interface Props {
    croppedImages: any;
    audioBlobURL: string;
    states: any[];
    audio: React.MutableRefObject<HTMLAudioElement[]>;
    playAudio: (idx: number) => void;
    audiolisteners: any;
    audioPlayingIdx: number;
    frameSpeed: number;
    setAudioPlayingIdx: (idx: number) => void;
    setDoneButtonDisabled: (disabled: boolean) => void;
    onUpdateAnnotations: (states: any[]) => void;
    onUpdateJobAttributes(id: number, value: string): void;
    jobAttributeSpec: any[];
    jobAttributes: any;
    popupNumber: number;
    jobInstance: any;
}

const DOESNOTAPPEAR_END = '.doesnotappear';
const DOESNOTCONTRIBUTE_END = '.doesnotcontribute';



function getRandomizedAttributesOrder(attrNames: string[], config: any) {
    // Based on the phase2 version, we will use different configurations
    const doesNotAppearAttr = attrNames.filter((attrName) => attrName.endsWith(DOESNOTAPPEAR_END));
    const doesNotContributeAttr = attrNames.filter((attrName) => attrName.endsWith(DOESNOTCONTRIBUTE_END));
    // group attributes like fear, feartype together
    const groupedAttrs = attrNames
        .filter((attrName) => (!attrName.endsWith(DOESNOTAPPEAR_END) && !attrName.endsWith(DOESNOTCONTRIBUTE_END)))
        .reduce((acc, attrName) => {
            const groupName = config[attrName]?.groupWith || attrName;
            if (acc[groupName]) {
                if (groupName == attrName) acc[groupName].unshift(attrName);
                else acc[groupName].push(attrName);
            } else {
                acc[groupName] = [attrName];
            }
            return acc;
        }, {} as {[groupName: string]: string[]});

    // shuffle the groups between each other, but keep the items within the group untouched
    const shuffledGroupKeys = Object.keys(groupedAttrs)
        .map((key) => ({ key, random:
            key.endsWith('emotionother') ? 999999 // hack to keep the open ended questions the last
            : Math.random() }))
        .sort((a, b) => a.random - b.random)
        .map(({ key }) => key);

    // flatten the shuffled group
    const flatShuffled = shuffledGroupKeys.reduce((acc, key) => acc.concat(groupedAttrs[key]), [] as string[]);

    // the "does not appear" checkbox should be first, followed by the "does not contribute checkbox", then everything else
    return doesNotAppearAttr.concat(doesNotContributeAttr).concat(flatShuffled);
}

function Phase2aPopupQuestionnaire(props: Props) {
    const {
        states,
        audioBlobURL,
        audio,
        croppedImages,
        audiolisteners,
        audioPlayingIdx,
        frameSpeed,
        jobAttributeSpec,
        jobAttributes,
        playAudio,
        setAudioPlayingIdx,
        setDoneButtonDisabled,
        onUpdateAnnotations,
        onUpdateJobAttributes,
        popupNumber,
        jobInstance
    } = props;
    useEffect(() => {
        setDoneButtonDisabled(true);
    }, []);

    useEffect(() => {
        setDoneButtonDisabled(false);
        return () => setDoneButtonDisabled(true);
    }, []);

    //Based on the phase2 version, we will use different configurations
    const config = jobInstance.phase2a_version === '2' ? config_V2 : config_V1;
    const getFilledFullText = jobInstance.phase2a_version === '2' ? getFilledFullText_V2 : getFilledFullText_V1;
    const toDisplay = jobInstance.phase2a_version === '2' ? toDisplay_V2 : toDisplay_V1;

    const STARTS_WITH = 'phase2a.popup.' + popupNumber + '.';

    const randomizedAttrIndicesByStateId = useRef(
        states.reduce((acc, state) => {
            const randomized = getRandomizedAttributesOrder(
                state.label.attributes
                .map((attr: any) => attr.name), config
            );
            acc[state.clientID] = randomized.reduce((indices, attrName, index) => {
                indices[attrName] = index;
                return indices;
            }, {} as {[attrName: string]: number})
            return acc;
        }, {} as {
            [clientID: number] : {
                [attrName: string] : number
            }
        }),
    );
    const [showDoesNotAppearError, setShowDoesNotAppearError] = useState<number[]>([]);
    const doesNotAppearCheck = (states: any) => {
        const arr: number[] = []
        const anyTrue = states.some((state: any, index: number) => {
            let attributes = state.label.attributes.filter((attr: any) => attr.name.endsWith(DOESNOTAPPEAR_END));
            let values = attributes.map((attr: any) => state.attributes[attr.id]);
            let finalStateValue = values.every((val: any) => val === 'true');

            if (finalStateValue) {
                arr.push(index);
            }

            return finalStateValue; // .some will return true if any finalStateValue is true
        });
        setShowDoesNotAppearError(arr);
        if (anyTrue) {
            setDoneButtonDisabled(true);
        }

    };

    useEffect(() => {
        doesNotAppearCheck(states);
    }, [states])

    /* We define a new state for only the textboxes since if we update the state directly,
     the component will re-render, this causes the cursor to jump to the start of the input field every time
     we can't use attribute.id as key since it is not unique across all attributes
     so we use the serverID as key the structure is like so:
    {
     serverID: {
        AttributeId: value
     }
    }
    */

    function initTextboxStates(inputArray: any): any {
        console.log(inputArray)
        let result: { [key: string]: { [key: string]: any } } = {};
            inputArray.forEach((item) => {
                if (!result[item.serverID]) {
                    result[item.serverID] = {};
                }
                item.label.attributes.forEach((attr) => {
                    if (attr.inputType === "text") {
                        result[item.serverID][attr.id] = item.attributes[attr.id] || '';
                    }
                });
            });

        return result;
    }

    const [textboxStates, setTextboxStates] = useState<{ any: any }>(initTextboxStates(states));

    const handleTextboxChange = (attrId: string, value: string, serverID: string, stateIndex: number) => {
        // hack to create copy of object
        let oldStates = JSON.parse(JSON.stringify(textboxStates));
        // set new values in the copy and update the state
        oldStates[serverID][attrId] = value;
        setTextboxStates(oldStates);

        // update the state in the main state
        states[stateIndex].attributes = {
            ...states[stateIndex].attributes,
            [attrId]: value,
        };

        onUpdateAnnotations([states[stateIndex]]);
    };

    const forceCheckDoesNotContribute = (states: any) =>{
        const doNotForceCheck = ['person:Civilian:Driver', 'person:Civilian:Passenger', 'person:Police:Primary','person:Police:NonPrimary']
        if(states.length > 4){
            const showing = new Set();
            states.forEach((state: any, index: number) => {
                if(!doNotForceCheck.includes(state.label.name) && !showing.has(state.label.name)){
                    showing.add(state.label.name);
                    let attributes = state.label.attributes.filter((attr: any) => attr.name.endsWith(DOESNOTCONTRIBUTE_END));
                    let values = attributes.map((attr: any) => state.attributes[attr.id]);
                    let valuesSet = values.every((val: any) => val !== '');
                    if(!valuesSet){
                        attributes.forEach((attr: any) => {
                            state.attributes = {
                                ...state.attributes,
                                [attr.id]: 'true'
                            };
                        });
                        onUpdateAnnotations([states[index]]);
                    }
                }
            })
        }
    }

    useEffect(() => {
        forceCheckDoesNotContribute(states);
    },[])

    return (
        <><Title level={3}>Popup #{popupNumber+1}</Title>
        <Tabs
            type='card'
            tabBarStyle={{ marginBottom: '0px' }}
            onChange={() => {
                audio.current.forEach((rf) => rf.pause());
                setAudioPlayingIdx(-1);
            }}
        >
            {states.map((state, idx) => (
                <Tabs.TabPane
                    tab={
                        <span>
                            <Text>
                                {getLabelDisplayName(state.label.name)}{' '}
                                {
                                    state.attributes[
                                        state.label.attributes?.find((attr: any) => attr.name === 'identifier').id
                                    ]
                                }
                            </Text>
                            {
                            // doesnotappear is unchecked, if it exists
                                (state.label.attributes.every(
                                    (attr: any) =>
                                        attr.name === STARTS_WITH + 'doesnotappear' ?
                                            ['false', ''].includes(state.attributes[attr.id]) : true,
                                ) &&
                            // doesnotcontribute is unchecked, if it exists
                                state.label.attributes.every(
                                    (attr: any) =>
                                        attr.name === STARTS_WITH + 'doesnotcontribute' ?
                                            ['false', ''].includes(state.attributes[attr.id]) : true,
                                ) &&
                                    // non-nullable attributes are unfilled
                                    state.label.attributes
                                        .filter((attr: any) => attr.name.startsWith(STARTS_WITH)
                                            && !attr.nullable && toDisplay(
                                                state.label.attributes,
                                                jobAttributeSpec,
                                                state.attributes,
                                                jobAttributes,
                                                attr,
                                                states
                                            ))
                                        .some((attr: any) => state.attributes[attr.id] === '') ||
                                    (showDoesNotAppearError.includes(idx)))
                                && (
                                    <CaretLeftFilled style={{ color: 'red' }} />
                                )} {
                                    // if doesnotcontribute is checked, show caret left yellow
                                    state.label.attributes.some(
                                        (attr: any) =>
                                            attr.name === STARTS_WITH + 'doesnotcontribute' ?
                                                ['true'].includes(state.attributes[attr.id]) : false,
                                    ) && (
                                        <CaretLeftFilled style={{ color: '#f5cb42' }} />
                                    )

                                }
                        </span>
                    }
                    key={state.clientID}
                >
                    <Row gutter={16}>
                        <Col span={18}>
                            {state.label.attributes
                                .filter((attr: any) => attr.name.startsWith(STARTS_WITH) && toDisplay(
                                    state.label.attributes,
                                    jobAttributeSpec,
                                    state.attributes,
                                    jobAttributes,
                                    attr,
                                    states
                                ))
                                .toSorted((a: any, b: any) => randomizedAttrIndicesByStateId.current[state.clientID][a.name] - randomizedAttrIndicesByStateId.current[state.clientID][b.name])
                                .map((attr: any) => (
                                    ((// doesnotappear is unchecked, if it exists
                                    state.label.attributes.every(
                                        (attr: any) =>
                                            attr.name === STARTS_WITH + 'doesnotappear' ?
                                            ['false', ''].includes(state.attributes[attr.id]) : true,
                                    ) ||
                                    // or this is the doesnotappear checkbox attribute (always show)
                                    attr.name.endsWith(DOESNOTAPPEAR_END)
                                    )&&(
                                    // doesnotcontribute is unchecked, if it exists
                                    state.label.attributes.every(
                                        (attr: any) =>
                                            attr.name === STARTS_WITH + 'doesnotcontribute' ?
                                            ['false', ''].includes(state.attributes[attr.id]) : true,
                                    ) ||
                                    // or this is the doesnotcontribute checkbox attribute (always show)
                                    attr.name.endsWith(DOESNOTCONTRIBUTE_END)
                                    ))
                                    &&
                                    <div>
                                        {showDoesNotAppearError.includes(idx) && (
                                            <div>
                                                <Text type="danger">The "does not appear" checkbox has been checked for all popups of {getLabelDisplayName(state.label.name)} {state.attributes[
                                                    state.label.attributes?.find((attr: any) => attr.name === 'identifier').id
                                                ]}.
                                                    This is not allowed. Please uncheck the "does not appear" checkbox for at least one popup of {getLabelDisplayName(state.label.name)} {state.attributes[
                                                        state.label.attributes?.find((attr: any) => attr.name === 'identifier').id
                                                    ]}.
                                                </Text>
                                            </div>
                                        )}
                                        <div style={{ margin: '25px 0 0 0' }}>
                                            <Text>{getFilledFullText(attr.name, state.label.name)}</Text>{' '}
                                            {

                                            // non-nullable attr has no value
                                            state.attributes[attr.id] === '' && !attr.nullable && (
                                                <CaretLeftFilled style={{ color: 'red' }} />
                                            )}
                                        </div>

                                        <div>
                                            {attr.inputType === 'checkbox' && (
                                                <Checkbox
                                                    onChange={(event: CheckboxChangeEvent): void => {
                                                        state.attributes = {
                                                            ...state.attributes,
                                                            [attr.id]: event.target.checked ? 'true' : 'false',
                                                        };
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    checked={state.attributes[attr.id] === 'true'}
                                                />
                                            )}
                                            {attr.inputType === 'number' && (
                                                <Row gutter={[16, 32]}>
                                                    <Col sm={4}>
                                                    </Col>
                                                    <Col sm={12}>
                                                        <Slider
                                                            included={false}
                                                            marks={{
                                                                ...Array((parseFloat(attr.values[1]) || 5) - (parseFloat(attr.values[0]) || 1) + 1).fill(0).reduce((prev, item, idx) => {
                                                                    prev[idx + (parseFloat(attr.values[0]) || 1)] = '';
                                                                    return prev
                                                                }, {}),

                                                                [parseFloat(attr.values[0]) || 1]: config[attr.name]?.low || '',
                                                                [((parseFloat(attr.values[0]) || 1) + (parseFloat(attr.values[1]) || 5))/2]: 'Neutral',
                                                                [parseFloat(attr.values[1]) || 5]: config[attr.name]?.high || '',
                                                            }}
                                                            min={parseFloat(attr.values[0]) || 1}
                                                            max={parseFloat(attr.values[1]) || 5}
                                                            step={parseFloat(attr.values[2]) || 1}
                                                            onChange={(val) => {
                                                                state.attributes = {
                                                                    ...state.attributes,
                                                                    [attr.id]: '' + val,
                                                                };
                                                                onUpdateAnnotations([states[idx]]);
                                                            }}
                                                            value={
                                                                state.attributes[attr.id] != ''
                                                                    ? parseInt(state.attributes[attr.id])
                                                                    : 3
                                                            }
                                                            style={{ width: '100%' }}
                                                        />
                                                </Col>
                                                <Col offset={2} span={4}>
                                                    <InputNumber
                                                    min={parseFloat(attr.values[0]) || 1}
                                                    max={parseFloat(attr.values[1]) || 5}
                                                    step={parseFloat(attr.values[2]) || 1}
                                                    style={{ margin: '0 16px' }}
                                                    value={state.attributes[attr.id] != ''
                                                        ? parseInt(state.attributes[attr.id])
                                                        : 3}
                                                    disabled = {true}
                                                    onChange={(val) => {
                                                        state.attributes = {
                                                            ...state.attributes,
                                                            [attr.id]: '' + val,
                                                        };
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    />
                                                </Col>
                                                </Row>
                                            )}
                                            {attr.inputType === 'select' && (
                                                <Select
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val) => {
                                                        state.attributes = {
                                                            ...state.attributes,
                                                            [attr.id]: val,
                                                        };
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    value={state.attributes[attr.id]}
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                            {attr.inputType === 'multiselect' && (
                                                <Select
                                                    mode='multiple'
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val: string[]) => {
                                                        state.attributes = {
                                                            ...state.attributes,
                                                            [attr.id]: val
                                                                .map((valitem: string) => valitem.trim())
                                                                .join(),
                                                        };
                                                        onUpdateAnnotations([states[idx]]);
                                                    }}
                                                    value={
                                                        state.attributes[attr.id]
                                                            ? state.attributes[attr.id]
                                                                  .split(',')
                                                                  .filter((valitem: string) => valitem.trim() !== '')
                                                            : []
                                                    }
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                            {attr.inputType === 'text' && (
                                                <AnnotationVoiceInput
                                                    value={textboxStates[state.serverID][attr.id] || ''}
                                                    onChange={(nextValue) => {
                                                        handleTextboxChange(attr.id, nextValue, state.serverID, idx);
                                                    }}
                                                    context={{
                                                        jobId: jobInstance.id,
                                                        phase: jobInstance.phase,
                                                        attributeSpecId: attr.id,
                                                        attributeName: attr.name,
                                                        targetScope: 'object',
                                                        targetServerId: state.serverID,
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <Divider style={{marginTop: '36px'}}></Divider>
                                    </div>
                                ))}
                                <Row style={{height: '32px'}}></Row>
                        </Col>
                        <Col span={6}>
                            {states[idx].objectType === 'audioselection' && audioBlobURL && (
                                <>
                                    <AudioPlayButton
                                        audioBlobURL={audioBlobURL}
                                        audio={audio}
                                        audioPlayingIdx={audioPlayingIdx}
                                        frameSpeed={frameSpeed}
                                        audiolisteners={audiolisteners}
                                        idx={idx}
                                        playAudio={playAudio}
                                        setAudioPlayingIdx={setAudioPlayingIdx}
                                        state={states[idx]}
                                    />
                                </>
                            )}
                            {states[idx].objectType === 'shape' && croppedImages[idx] && (
                                <img style={{ maxWidth: '100%', height: '150px' }} src={croppedImages[idx]}></img>
                            )}
                        </Col>
                    </Row>
                </Tabs.TabPane>
            ))}

            {jobAttributeSpec.filter((spec) => spec.name.startsWith(STARTS_WITH)).length && (
                <Tabs.TabPane
                    tab={
                        <span>
                            <Text>Situation Labels</Text>
                            {jobAttributeSpec
                                .filter((spec) => spec.name.startsWith(STARTS_WITH))
                                .some((attr: any) => jobAttributes[attr.id] === '') && (
                                <CaretLeftFilled style={{ color: 'red' }} />
                            )}
                        </span>
                    }
                    key={9999999999}
                >
                    <Row gutter={16}>
                        <Col span={18}>
                            {jobAttributeSpec
                                .filter((attr: any) => attr.name.startsWith(STARTS_WITH))
                                .toSorted((a: any, b: any) => a.id - b.id)
                                .map((attr: any) => (
                                    <div>
                                        <div style={{ margin: '25px 0 0 0' }}>
                                            <Text>{config[attr.name]?.fullText}</Text>{' '}
                                            {jobAttributes[attr.id] === '' && (
                                                <CaretLeftFilled style={{ color: 'red' }} />
                                            )}
                                        </div>
                                        <div>
                                            {attr.inputType === 'number' && (
                                                <Row gutter={[16, 32]}>
                                                <Col sm={4}>
                                                </Col>
                                                <Col sm={12}>
                                                    <Slider
                                                        included={false}
                                                        marks={{
                                                            [parseFloat(attr.values[0]) || 1]: config[attr.name]?.low || '',
                                                            [((parseFloat(attr.values[0]) || 1) + (parseFloat(attr.values[1]) || 5))/2]: 'Neutral',
                                                            [parseFloat(attr.values[1]) || 5]: config[attr.name]?.high || '',
                                                        }}
                                                        min={parseFloat(attr.values[0]) || 1}
                                                        max={parseFloat(attr.values[1]) || 5}
                                                        step={parseFloat(attr.values[2]) || 1}
                                                        onChange={(val) => {
                                                            onUpdateJobAttributes(attr.id, '' + val);
                                                        }}
                                                        value={jobAttributes[attr.id]}
                                                        style={{ width: '100%' }}
                                                    />
                                            </Col>
                                            <Col offset={2} span={4}>
                                                <InputNumber
                                                min={parseFloat(attr.values[0]) || 1}
                                                max={parseFloat(attr.values[1]) || 5}
                                                step={parseFloat(attr.values[2]) || 1}
                                                style={{ margin: '0 16px' }}
                                                onChange={(val) => {
                                                    onUpdateJobAttributes(attr.id, '' + val);
                                                }}
                                                value={jobAttributes[attr.id]}
                                                />
                                            </Col>
                                            </Row>
                                            )}
                                            {attr.inputType === 'select' && (
                                                <Select
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val) => {
                                                        onUpdateJobAttributes(attr.id, val);
                                                    }}
                                                    value={jobAttributes[attr.id]}
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                            {attr.inputType === 'multiselect' && (
                                                <Select
                                                    mode='multiple'
                                                    options={attr.values.map((val: string) => ({
                                                        label: val,
                                                        value: val,
                                                    }))}
                                                    onChange={(val: string[]) => {
                                                        onUpdateJobAttributes(
                                                            attr.id,
                                                            val.map((valitem: string) => valitem.trim()).join(),
                                                        );
                                                    }}
                                                    value={
                                                        jobAttributes[attr.id]
                                                            ? jobAttributes[attr.id]
                                                                  .split(',')
                                                                  .filter((valitem: string) => valitem.trim() !== '')
                                                            : []
                                                    }
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                            {attr.inputType === 'text' && (
                                                <AnnotationVoiceInput
                                                    value={jobAttributes[attr.id]}
                                                    onChange={(nextValue) => {
                                                        onUpdateJobAttributes(attr.id, nextValue);
                                                    }}
                                                    context={{
                                                        jobId: jobInstance.id,
                                                        phase: jobInstance.phase,
                                                        attributeSpecId: attr.id,
                                                        attributeName: attr.name,
                                                        targetScope: 'job',
                                                        targetServerId: null,
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </Col>
                    </Row>
                    <Row style={{height: '32px'}}></Row>
                </Tabs.TabPane>
            )}
        </Tabs></>
    );
}

export default Phase2aPopupQuestionnaire;
